import React, { Fragment, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, createMenuItems, CreateMenuItemsProps, OrganizationAvatar } from '@Components';
import styles from './UserAvatarMenu.module.scss';
import {
  RiDashboardLine,
  RiLogoutBoxRLine,
  RiUserLine,
  RiArrowDownSFill,
  RiSettings5Line,
} from 'react-icons/ri';

interface UserAvatarMenuProps {
  anchorEl: MutableRefObject<HTMLButtonElement | null>;
  isMenuOpen: boolean;
  avatarContent?: string;
  avatarImage?: string;
  userName?: string;
  userEmail?: string;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  onSignOut: () => void;
  onProfileClick: () => void;
  onDashboardClick: () => void;
  onAccountClick?: () => void;
  canModifyOrganizationProfile?: boolean;
  publicLayout?: boolean;
}

export const UserAvatarMenu: React.FC<UserAvatarMenuProps> = ({
  anchorEl,
  isMenuOpen,
  avatarContent,
  avatarImage,
  userName,
  userEmail,
  onMenuOpen,
  onMenuClose,
  onSignOut,
  onProfileClick,
  onDashboardClick,
  onAccountClick,
  canModifyOrganizationProfile,
  publicLayout,
}) => {
  const { t } = useTranslation();

  const dropDownMenuItems: CreateMenuItemsProps['items'] = [
    {
      label: (
        <div>
          <span className={styles.userName}>{userName}</span>
          <span className={styles.userEmail}>{userEmail}</span>
        </div>
      ),
      menuItemProps: {
        disabled: true,
      },
    },
    {
      label: t('common:nav.home'),
      menuItemProps: {
        onClick: onDashboardClick,
      },
      icon: <RiDashboardLine />,
    },
    {
      label: t('common:nav.profile'),
      menuItemProps: {
        onClick: onProfileClick,
      },
      icon: <RiUserLine />,
    },
    // Only show account option if user can modify organization profile
    ...(canModifyOrganizationProfile && onAccountClick
      ? [
          {
            label: t('common:nav.account'),
            menuItemProps: {
              onClick: onAccountClick,
            },
            icon: <RiSettings5Line />,
          },
        ]
      : []),
    {
      label: t('common:nav.signout'),
      menuItemProps: {
        onClick: onSignOut,
      },
      icon: <RiLogoutBoxRLine />,
    },
  ];

  return (
    <Fragment>
      <button
        aria-label="user-menu"
        onClick={onMenuOpen}
        className={styles.container}
        ref={anchorEl}
      >
        <OrganizationAvatar size="small" avatarImage={avatarImage}>
          {avatarContent}
        </OrganizationAvatar>
        <RiArrowDownSFill color={publicLayout ? 'white' : 'black'} />
      </button>
      <Menu open={isMenuOpen} anchorEl={anchorEl.current} onClose={onMenuClose}>
        {createMenuItems({ items: dropDownMenuItems })}
      </Menu>
    </Fragment>
  );
};
